import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class GoogleTrackingService {
  private readonly currency = 'BRL';

  trackBeginCheckout(pedido: any): boolean {
    const items = this.buildItems(pedido);
    if (!items.length) return false;

    return this.dispatchEcommerceEvent('begin_checkout', {
      currency: this.currency,
      value: this.resolvePedidoValue(pedido),
      coupon: this.resolveCupom(pedido),
      items
    });
  }

  trackPurchase(pedido: any, transactionId: string): boolean {
    const items = this.buildItems(pedido);
    if (!transactionId || !items.length) return false;

    return this.dispatchEcommerceEvent('purchase', {
      currency: this.currency,
      transaction_id: transactionId,
      value: this.resolvePedidoValue(pedido),
      shipping: this.resolveShipping(pedido),
      discount: this.resolveDiscount(pedido),
      coupon: this.resolveCupom(pedido),
      items
    });
  }

  private dispatchEcommerceEvent(eventName: string, ecommerce: any): boolean {
    if (typeof window === 'undefined') return false;

    const payload = this.removeEmptyValues(ecommerce);
    const win = window as any;

    if (typeof win.gtag === 'function') {
      win.gtag('event', eventName, payload);
      return true;
    }

    if (win.dataLayer && typeof win.dataLayer.push === 'function') {
      win.dataLayer.push({ ecommerce: null });
      win.dataLayer.push({
        event: eventName,
        ecommerce: payload
      });
      return true;
    }

    return false;
  }

  private buildItems(pedido: any): any[] {
    const itens = Array.isArray(pedido?.itens) ? pedido.itens : [];

    return itens
      .filter((item: any) => item && item.qtde > 0 && item.produto)
      .map((item: any) => {
        const produto = item.produto || {};
        const qtde = Math.max(1, Number(item.qtde || 1));
        const unitPrice = this.round2(
          Number(item.valorUnitario || item.obtenhaValorUnitario?.() || (Number(item.total || 0) / qtde) || 0)
        );

        const payload: any = {
          item_id: String(produto.id || item.id || ''),
          item_name: produto.nome || item.nome || 'Item',
          quantity: qtde,
          price: unitPrice
        };

        const categoria = produto?.categoria?.nome || produto?.categoria || null;
        const variante = item?.produtoTamanho?.descricao || item?.produtoTamanho?.nome || null;

        if (categoria) {
          payload.item_category = categoria;
        }

        if (variante) {
          payload.item_variant = variante;
        }

        return payload;
      })
      .filter((item: any) => item.item_id && item.item_name);
  }

  private resolvePedidoValue(pedido: any): number {
    const value = typeof pedido?.obtenhaValorAhPagar === 'function'
      ? pedido.obtenhaValorAhPagar()
      : pedido?.total;

    return this.round2(Number(value || 0));
  }

  private resolveShipping(pedido: any): number {
    const shipping = pedido?.entrega?.taxaDeEntrega;
    return this.round2(Number(shipping || 0));
  }

  private resolveDiscount(pedido: any): number {
    const descontoTotal = Number(pedido?.desconto || 0)
      + Number(pedido?.descontoFormaDeEntrega || 0)
      + Number(pedido?.descontoFormaDePagamento || 0);

    return this.round2(descontoTotal);
  }

  private resolveCupom(pedido: any): string | null {
    const codigo = pedido?.cupom?.codigo || pedido?.codigoCupom || null;
    return codigo ? String(codigo).trim() : null;
  }

  private round2(value: number): number {
    return Math.round(Number(value || 0) * 100) / 100;
  }

  private removeEmptyValues(payload: any): any {
    return Object.keys(payload || {}).reduce((acc: any, key: string) => {
      const value = payload[key];

      if (value == null) {
        return acc;
      }

      if (Array.isArray(value) && value.length === 0) {
        return acc;
      }

      if (typeof value === 'string' && value.trim() === '') {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {});
  }
}
